(() => {
  const validDates = value => {
    let dates;
    try { dates = JSON.parse(value || '[]'); } catch { return []; }
    return [...new Set((Array.isArray(dates) ? dates : [dates]).filter(date =>
      typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date) &&
      !Number.isNaN(Date.parse(date)) && new Date(date).toISOString().slice(0, 10) === date
    ))].sort();
  };
  const formatDates = dates => {
    if (!dates.length) return 'Dates to be announced';
    const format = (date, options) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', ...options }).format(new Date(`${date}T12:00:00Z`));
    const full = date => format(date, { month: 'long', day: 'numeric', year: 'numeric' });
    if (dates.length === 1) return full(dates[0]);
    const contiguous = dates.every((date, i) => !i || Date.parse(date) - Date.parse(dates[i - 1]) === 86400000);
    if (!contiguous) return dates.map(full).join(' · ');
    const first = dates[0], last = dates.at(-1);
    if (first.slice(0, 7) === last.slice(0, 7)) return `${format(first, { month: 'long', day: 'numeric' })}–${format(last, { day: 'numeric' })}, ${last.slice(0, 4)}`;
    if (first.slice(0, 4) === last.slice(0, 4)) return `${format(first, { month: 'long', day: 'numeric' })}–${full(last)}`;
    return `${full(first)}–${full(last)}`;
  };
  class IndustryEvents extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        const map = this.querySelector('[data-map]');
        if (map) this.mapObserver?.observe(map);
        return;
      }
      this.initialized = true;
      const today = this.dataset.today;
      const events = [...this.querySelectorAll('[data-event]')].map((card, index) => {
        const dates = validDates(card.dataset.dates);
        const start = dates[0], end = dates.at(-1);
        const state = !start ? 'undated' : end < today ? 'past' : start <= today ? 'current' : 'upcoming';
        card.id = `${this.closest('[id]')?.id || 'industry-events'}-event-${index}`;
        card.dataset.state = state;
        card.querySelector('[data-date]').textContent = formatDates(dates);
        card.querySelector('[data-status]').textContent = { undated: 'Dates to be announced', past: 'Past event', current: 'Happening now', upcoming: 'Upcoming' }[state];
        if (state === 'past') {
          card.querySelector('.ie-meet')?.remove();
          const booth = card.querySelector('.ie-booth');
          if (booth) booth.remove();
        }
        return { card, start, end, state };
      });
      const upcoming = events.filter(e => ['current', 'upcoming'].includes(e.state)).sort((a, b) => a.start.localeCompare(b.start));
      const past = events.filter(e => e.state === 'past').sort((a, b) => b.end.localeCompare(a.end));
      const undated = events.filter(e => e.state === 'undated');
      const featured = upcoming[0];
      if (featured) {
        featured.card.classList.add('ie-card-featured');
        featured.card.querySelector('[data-status]').textContent = featured.state === 'current' ? 'Happening now' : 'Next up · Meet us there';
        const feature = this.querySelector('[data-feature]');
        feature.append(featured.card);
        feature.hidden = false;
      } else this.querySelector('[data-empty]').hidden = false;
      upcoming.slice(1).forEach(e => this.querySelector('[data-upcoming]').append(e.card));
      this.querySelector('[data-upcoming-section]').hidden = upcoming.length <= 1;
      this.querySelector('[data-upcoming-count]').textContent = `${Math.max(0, upcoming.length - 1)} more on the calendar`;
      past.forEach(e => this.querySelector('[data-past]').append(e.card));
      this.querySelector('[data-archive]').hidden = !past.length;
      this.querySelector('[data-past-count]').textContent = `(${past.length})`;
      undated.forEach(e => this.querySelector('[data-undated]').append(e.card));
      this.querySelector('[data-undated-section]').hidden = !undated.length;
      this.renderMap(events);
    }
    renderMap(events) {
      const map = this.querySelector('[data-map]');
      if (!map) return;
      const number = (key, fallback) => map.dataset[key] !== '' && Number.isFinite(Number(map.dataset[key])) ? Number(map.dataset[key]) : fallback;
      const minLat = number('minLat', 24), maxLat = number('maxLat', 50), minLng = number('minLng', -125), maxLng = number('maxLng', -66);
      if (maxLat <= minLat || maxLng <= minLng) return;
      const points = [];
      events.forEach(event => {
        if (!event.card.dataset.lat || !event.card.dataset.lng) return;
        const lat = Number(event.card.dataset.lat), lng = Number(event.card.dataset.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < minLat || lat > maxLat || lng < minLng || lng > maxLng) return;
        const x = (((lng - minLng) / (maxLng - minLng) * 100 - 50) * number('scaleX', 1)) + 50 + number('offsetX', 0);
        const y = ((1 - (lat - minLat) / (maxLat - minLat)) * 100 - 50) * number('scaleY', 1) + 50 + number('offsetY', 0);
        if (x >= 0 && x <= 100 && y >= 0 && y <= 100) points.push({ x, y, event });
      });
      const image = map.querySelector('.ie-map-image');
      const markers = document.createElement('div');
      markers.className = 'ie-markers';
      const toolbar = document.createElement('div');
      toolbar.className = 'ie-map-toolbar';
      const reset = document.createElement('button');
      reset.type = 'button'; reset.className = 'ie-map-reset';
      reset.textContent = '← Back to full map'; reset.hidden = true;
      toolbar.append(reset);
      const locations = document.createElement('div');
      locations.className = 'ie-map-locations'; locations.hidden = true;
      map.before(toolbar);
      map.after(locations);
      // Keep toolbar, map and location choices together in the layout.
      const frame = document.createElement('div');
      frame.className = 'ie-map-frame';
      toolbar.before(frame); frame.append(toolbar, map, locations);
      map.append(markers);
      let activeGroup = null;
      let lastCluster = null;
      const title = point => point.event.card.querySelector('h3').textContent;
      const status = point => ({ past: 'past event', current: 'happening now', upcoming: 'upcoming', undated: 'dates to be announced' })[point.event.state];
      const select = point => {
        this.querySelectorAll('.ie-card-selected').forEach(card => card.classList.remove('ie-card-selected'));
        const card = point.event.card;
        card.classList.add('ie-card-selected');
        if (point.event.state === 'past') this.querySelector('[data-archive]').open = true;
        this.querySelector('[data-map-result]').textContent = title(point);
        card.tabIndex = -1;
        card.focus({ preventScroll: true });
        card.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
      };
      const draw = () => {
        markers.replaceChildren(); locations.replaceChildren();
        const width = map.clientWidth || 600, height = map.clientHeight || width * .62;
        let scale = 1, cx = 50, cy = 50;
        if (activeGroup) {
          const xs = activeGroup.map(p => p.x), ys = activeGroup.map(p => p.y);
          cx = (Math.min(...xs) + Math.max(...xs)) / 2;
          cy = (Math.min(...ys) + Math.max(...ys)) / 2;
          scale = Math.min(6, Math.max(2, Math.min(65 / Math.max(1, Math.max(...xs) - Math.min(...xs)), 60 / Math.max(1, Math.max(...ys) - Math.min(...ys)))));
          cx = Math.max(50 / scale, Math.min(100 - 50 / scale, cx));
          cy = Math.max(50 / scale, Math.min(100 - 50 / scale, cy));
        }
        if (image) {
          image.style.transformOrigin = '0 0';
          image.style.transform = `translate(${50 - cx * scale}%, ${50 - cy * scale}%) scale(${scale})`;
        }
        reset.hidden = !activeGroup; locations.hidden = !activeGroup;
        const groups = [];
        (activeGroup || points).forEach(point => {
          let group = !activeGroup && groups.find(g => Math.hypot((g[0].x - point.x) * width / 100, (g[0].y - point.y) * height / 100) < 44);
          if (!group) { group = []; groups.push(group); }
          group.push(point);
        });
        // Coordinates occupying the same pixel area get one open location anchor and a spoke to every
        // event, including the first. Keep anchors clear when resolving collisions.
        const shared = [];
        if (activeGroup) {
          activeGroup.forEach(point => {
            let location = shared.find(site => Math.hypot((site.x - point.x) * scale * width / 100, (site.y - point.y) * scale * height / 100) < 2);
            if (!location) { location = { x: point.x, y: point.y, points: [] }; shared.push(location); }
            location.points.push(point);
          });
        }
        const anchors = shared.filter(site => site.points.length > 1).map(site => ({
          ...site, screenX: (50 + (site.x - cx) * scale) * width / 100,
          screenY: (50 + (site.y - cy) * scale) * height / 100
        }));
        anchors.forEach(site => {
          const anchor = document.createElement('span');
          anchor.className = 'ie-map-anchor'; anchor.setAttribute('aria-hidden', 'true');
          anchor.style.left = `${site.screenX}px`; anchor.style.top = `${site.screenY}px`;
          markers.append(anchor);
        });
        const placed = anchors.map(site => ({ x: site.screenX, y: site.screenY }));
        groups.forEach(group => {
          const point = group[0];
          const trueX = (50 + (point.x - cx) * scale) * width / 100;
          const trueY = (50 + (point.y - cy) * scale) * height / 100;
          let x = trueX, y = trueY;
          if (activeGroup) {
            const site = anchors.find(anchor => anchor.points.includes(point));
            if (site) {
              // Fan toward the roomier side of the map, with enough spacing
              // between neighboring event targets to keep each selectable.
              const spread = 2 * Math.PI / 3;
              const direction = trueX <= width / 2 ? 0 : Math.PI;
              const angle = direction - spread / 2 + site.points.indexOf(point) * spread / (site.points.length - 1);
              const radius = Math.max(52, 46 / (2 * Math.sin(spread / (2 * (site.points.length - 1)))));
              x = Math.max(24, Math.min(width - 24, trueX + Math.cos(angle) * radius));
              y = Math.max(24, Math.min(height - 24, trueY + Math.sin(angle) * radius));
            }
            for (let attempt = 0; attempt < 200 && placed.some(p => Math.hypot(p.x - x, p.y - y) < 44); attempt++) {
              const angle = attempt * 2.4, radius = 46 + Math.floor(attempt / 8) * 20;
              x = Math.max(24, Math.min(width - 24, trueX + Math.cos(angle) * radius));
              y = Math.max(24, Math.min(height - 24, trueY + Math.sin(angle) * radius));
            }
            placed.push({ x, y });
            if (Math.hypot(x - trueX, y - trueY) > 1) {
              const line = document.createElement('span');
              line.className = 'ie-map-leader';
              line.style.cssText = `left:${trueX}px;top:${trueY}px;width:${Math.hypot(x - trueX, y - trueY)}px;transform:rotate(${Math.atan2(y - trueY, x - trueX)}rad)`;
              markers.append(line);
            }
          }
          const pin = document.createElement('button');
          const onlyPast = group.every(p => p.event.state === 'past');
          pin.type = 'button';
          pin.className = `ie-pin${onlyPast ? ' ie-pin-past' : ''}${group.length > 1 ? ' ie-pin-group' : ''}`;
          pin.style.left = `${x}px`; pin.style.top = `${y}px`;
          const icon = document.createElement('span'); icon.className = 'ie-pin-icon'; icon.setAttribute('aria-hidden', 'true');
          icon.textContent = onlyPast ? '◆' : '●'; pin.append(icon);
          if (group.length > 1) {
            const count = document.createElement('span'); count.className = 'ie-pin-count'; count.textContent = group.length; count.setAttribute('aria-hidden', 'true'); pin.append(count);
          }
          const label = group.length > 1 ? `Zoom in to ${group.length} events: ${group.map(title).join(', ')}` : `${title(point)}, ${point.event.card.dataset.city}, ${status(point)}`;
          pin.setAttribute('aria-label', label); pin.title = label;
          pin.addEventListener('click', () => {
            if (group.length === 1) { select(point); return; }
            lastCluster = point;
            activeGroup = group; draw(); reset.focus({ preventScroll: true });
            this.querySelector('[data-map-result]').textContent = `${group.length} events in this area. Select a marker or an event below the map. An open circle marks a shared venue, with a line to each event.`;
          });
          markers.append(pin);
          if (activeGroup) {
            const choice = document.createElement('button');
            choice.type = 'button'; choice.className = 'ie-map-choice';
            choice.textContent = `${title(point)} · ${status(point)}`;
            choice.addEventListener('click', () => select(point)); locations.append(choice);
          }
          if (!activeGroup && group.includes(lastCluster)) pin.dataset.returnFocus = 'true';
        });
      };
      reset.addEventListener('click', () => {
        activeGroup = null; draw();
        markers.querySelector('[data-return-focus]')?.focus({ preventScroll: true });
        this.querySelector('[data-map-result]').textContent = 'Showing all event locations.';
      });
      this.mapObserver = new ResizeObserver(draw);
      this.mapObserver.observe(map);
      draw();
    }
    disconnectedCallback() {
      this.mapObserver?.disconnect();

    }
  }
  if (!customElements.get('industry-events')) customElements.define('industry-events', IndustryEvents);
})();
